import styled, { css } from 'styled-components';

import PartNumberSelect from '../../../commonComponentsMC/PartNumberSelect';
import { makeBlankSpec } from '../../../../helpers/setting';
import {
  flexBoxCenter,
  justifyContentSpaceAround,
  layerA,
} from '../../../styles/commonStyles';

// Part numbers are compared the way the admin SSR screen compares them, so a
// difference in case or padding between the catalogue and a stored heater does
// not read as a different part.
const samePartNumber = (a, b) =>
  String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();

// One heat-load spec line: the part-number picker plus the figures read off
// whichever heater it points at. The picker is the same control the admin SSR
// screen uses, so a part number is chosen the same way in both places.
//
// The picker deals in part-number strings while the form stores whole heater
// objects, so this component is the adapter between them.
const HeatLoadSpecRow = ({
  options,
  handleSelectedPartNumber,
  selectedSSRIdx,
  SSR,
  elIdx,
  elementIdx,
  el,
}) => {
  const catalogue = options ?? [];

  // A heater already on this SSR may have since left the catalogue. Without an
  // option for it the picker renders blank and the next save swaps it out.
  const partNumbers = catalogue.map(({ partNumber }) => partNumber);
  const isOffCatalogue =
    el?.partNumber && !partNumbers.some((pn) => samePartNumber(pn, el.partNumber));

  const handleChange = (partNumber) => {
    const chosen = partNumber
      ? catalogue.find(({ partNumber: pn }) => samePartNumber(pn, partNumber))
      : null;

    // Blank is how a row is put back to unanswered. Reporting the blank spec
    // rather than nothing keeps the figures beside it in step with the picker.
    handleSelectedPartNumber(
      chosen ?? makeBlankSpec(),
      selectedSSRIdx,
      SSR,
      elIdx,
      elementIdx,
    );
  };

  return (
    <Row>
      <Spec isPartNum={true}>
        <PartNumberSelect
          value={el?.partNumber ?? ''}
          options={isOffCatalogue ? [...partNumbers, el.partNumber] : partNumbers}
          onChange={handleChange}
        />
      </Spec>

      {/* figures of the chosen heater */}
      <Flex>
        <Spec isCurrent={true}>
          {el.currentCurrent ? el.currentCurrent + ' ' + 't' : ''}
        </Spec>
        <Spec isCurrent={true}>{el.current ? el.current + ' ' + 'a' : ''}</Spec>
      </Flex>
      <Spec isCommonSize={true}>
        {el.wattage ? el.wattage + ' ' + 'w' : ''}
      </Spec>
      <Spec isCommonSize={true}>
        {el.voltage ? el.voltage + ' ' + 'v' : ''}
      </Spec>
      <Spec isCommonSize={true}>
        {el.lengths ? el.lengths + ' ' + 'ft' : ''}
      </Spec>
      {el.elementName &&
      el.partNumber &&
      el.current &&
      el.wattage &&
      el.voltage &&
      el.lengths ? (
        <Spec isDescription={true}>
          {el.elementName} - {el.partNumber}/{el.current + ' ' + 'a'}/
          {el.wattage + ' ' + 'w'}/{el.voltage + ' ' + 'v'}/
          {el.lengths + ' ' + 'ft'}
        </Spec>
      ) : (
        <Spec isDescription={true}></Spec>
      )}
    </Row>
  );
};

export default HeatLoadSpecRow;

// 30px rather than the 20px this row used to be, so a long part number wraps
// inside the narrow column instead of being clipped by it — the same height
// and the same reason as ROW_HEIGHT in HeaterRow.
const Row = styled.div`
  width: 84%;
  height: 30px;
  margin-top: 4px;
  ${justifyContentSpaceAround}

  position: relative;
`;

const Flex = styled.div`
  height: 100%;
  display: flex;
  gap: 2px;
`;

const Spec = styled.div`
  height: 100%;

  font-size: 8px;
  letter-spacing: 0.8px;
  text-align: center;

  ${({ isPartNum, isCurrent, isCommonSize, isDescription }) =>
    isPartNum
      ? css`
          width: 76px;
          position: relative;
        `
      : isCurrent
        ? css`
            width: 42px;
          `
        : isCommonSize
          ? css`
              width: 72px;
            `
          : isDescription &&
            css`
              width: 260px;
            `}

  ${layerA}

  border-radius: 12px;

  ${flexBoxCenter}
`;
